import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaBell,
  FaBolt,
  FaCheck,
  FaEdit,
  FaPaperPlane,
  FaPlus,
  FaSpinner,
  FaSync,
  FaTrash,
  FaUpload,
  FaEye,
  FaChevronDown,
  FaChevronUp,
  FaBold,
  FaItalic,
  FaStrikethrough,
} from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useApp from "../../hooks/useAppContext";
import {
  atualizarNotificacaoWhatsapp,
  criarNotificacaoWhatsapp,
  criarTemplateWhatsapp,
  dispararNotificacaoWhatsapp,
  disparoRapidoWhatsapp,
  excluirNotificacaoWhatsapp,
  listarEnviosNotificacaoWhatsapp,
  listarNotificacoesWhatsapp,
  listarTemplatesWhatsapp,
  renderPreviewPlaceholders,
  extrairPlaceholdersTemplate,
  valoresPlaceholderDoUsuario,
  sincronizarTemplateWhatsapp,
  submeterTemplateWhatsapp,
  importarTemplatesTwilio,
} from "../../services/notificacaoWhatsappApi";
import "./admPatrocinadoresList.css";
import "./admFormPatrocinadores.css";
import "./admNotificacoes.css";

const TABS = [
  { id: "lista", label: "Mensagens", Icon: FaBell },
  { id: "form", label: "Nova mensagem", Icon: FaPlus },
  { id: "templates", label: "Templates Meta", Icon: FaCheck },
  { id: "disparo", label: "Disparo rápido", Icon: FaBolt },
];

const STATUS_LABEL = {
  rascunho: "Rascunho",
  agendada: "Agendada",
  enviando: "Enviando",
  enviada: "Enviada",
  cancelada: "Cancelada",
  erro: "Erro",
  unsubmitted: "Não submetido",
  received: "Recebido",
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
  paused: "Pausado",
  disabled: "Desabilitado",
};

const TIPOS_USUARIO_OPCOES = [
  { value: "convidado", label: "Convidado" },
  { value: "patrocinador", label: "Patrocinador" },
  { value: "participante_patrocinador", label: "Participante patrocinador" },
  { value: "administracao", label: "Administração" },
  { value: "administracao_dash", label: "Admin dashboard" },
  { value: "recepcao", label: "Recepção" },
];

const formVazio = () => ({
  id: null,
  mensagem: "",
  objetivo: "",
  quantidade_envios: 0,
  publico: "todos",
  tipos_usuarios: ["convidado"],
  data_hora_envio: "",
  status: "agendada",
  id_template: "",
  links: [{ url: "", rotulo: "" }],
  ids_usuarios: [],
  imagemFile: null,
  imagemPreview: null,
});

const templateVazio = () => ({
  nome: "",
  friendly_name: "",
  corpo: "Olá {{NOME}}, bem-vindo(a) ao evento.",
  categoria: "UTILITY",
  links: [{ url: "", rotulo: "" }],
  imagemFile: null,
});

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("pt-BR");
}

function toDatetimeLocalValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local → ISO local sem converter para UTC (evita +3h em America/Sao_Paulo). */
function datetimeLocalParaIso(valor) {
  if (!valor) return null;
  const bruto = String(valor).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(bruto)) {
    return `${bruto}:00`;
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(bruto)) {
    return bruto;
  }
  return null;
}

function Badge({ status }) {
  const key = String(status || "").toLowerCase();
  return (
    <span className={`anw-badge anw-badge--${key}`}>
      {STATUS_LABEL[key] || status || "—"}
    </span>
  );
}

/** Aplica formatação WhatsApp (*negrito*, _itálico_, ~riscado~) na seleção do textarea. */
function aplicarFormatacaoWhatsapp(textarea, value, setValue, marcaIni, marcaFim) {
  const texto = value || "";
  if (!textarea) {
    setValue(`${texto}${marcaIni}texto${marcaFim}`);
    return;
  }
  const start = textarea.selectionStart ?? texto.length;
  const end = textarea.selectionEnd ?? texto.length;
  const selecionado = texto.slice(start, end) || "texto";
  const next =
    texto.slice(0, start) + marcaIni + selecionado + marcaFim + texto.slice(end);
  setValue(next);
  requestAnimationFrame(() => {
    try {
      textarea.focus();
      const cursor = start + marcaIni.length + selecionado.length + marcaFim.length;
      textarea.setSelectionRange(cursor, cursor);
    } catch {
      /* ignore */
    }
  });
}

function formatarPreviewWhatsapp(texto) {
  const escaped = String(texto || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped
    .replace(/\*(.+?)\*/g, "<strong>$1</strong>")
    .replace(/_(.+?)_/g, "<em>$1</em>")
    .replace(/~(.+?)~/g, "<s>$1</s>")
    .replace(/\n/g, "<br />");
}

function FormatToolbarWhatsapp({ onBold, onItalic, onStrike }) {
  return (
    <div className="anw-format-bar" role="toolbar" aria-label="Formatação WhatsApp">
      <button type="button" className="anw-format-btn" onClick={onBold} title="Negrito (*texto*)">
        <FaBold />
      </button>
      <button type="button" className="anw-format-btn" onClick={onItalic} title="Itálico (_texto_)">
        <FaItalic />
      </button>
      <button
        type="button"
        className="anw-format-btn"
        onClick={onStrike}
        title="Riscado (~texto~)"
      >
        <FaStrikethrough />
      </button>
      <span className="anw-format-hint">
        WhatsApp: *negrito* · _itálico_ · ~riscado~
      </span>
    </div>
  );
}

function PreviewWhatsapp({ texto }) {
  if (!texto) return <span>—</span>;
  return (
    <div
      className="anw-wa-rich"
      dangerouslySetInnerHTML={{ __html: formatarPreviewWhatsapp(texto) }}
    />
  );
}

const AdmNotificacoes = () => {
  const navigate = useNavigate();
  const { getUsuariosLista } = useApp();

  const [tab, setTab] = useState("lista");
  const [loading, setLoading] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [convidados, setConvidados] = useState([]);
  const [buscaDest, setBuscaDest] = useState("");
  const [form, setForm] = useState(formVazio());
  const [templateForm, setTemplateForm] = useState(templateVazio());
  const [salvando, setSalvando] = useState(false);
  const [disparo, setDisparo] = useState({
    mensagem: "",
    numero: "",
    id_usuario: "",
    id_template: "",
    placeholders: {},
  });
  const [enviosModal, setEnviosModal] = useState({ open: false, items: [], titulo: "" });
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateFormAberto, setTemplateFormAberto] = useState(false);
  const refCorpoTemplate = useRef(null);
  const refMensagemDisparo = useRef(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [msgs, tpls, users] = await Promise.all([
        listarNotificacoesWhatsapp(),
        listarTemplatesWhatsapp(),
        getUsuariosLista(),
      ]);
      setMensagens(msgs || []);
      setTemplates(tpls || []);
      const lista = Array.isArray(users) ? users : [];
      setConvidados(
        lista.filter((u) =>
          TIPOS_USUARIO_OPCOES.some((t) => t.value === u?.tipo)
        )
      );
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar notificações WhatsApp");
    } finally {
      setLoading(false);
    }
  }, [getUsuariosLista]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const templatesAprovados = useMemo(
    () => templates.filter((t) => t.statusAprovacao === "approved" || t.status_aprovacao === "approved"),
    [templates]
  );

  const disparoTemplateSelecionado = useMemo(() => {
    if (!disparo.id_template) return null;
    return templates.find((t) => String(t.id) === String(disparo.id_template)) || null;
  }, [disparo.id_template, templates]);

  const disparoPlaceholdersKeys = useMemo(() => {
    if (disparoTemplateSelecionado) {
      return extrairPlaceholdersTemplate(disparoTemplateSelecionado);
    }
    return extrairPlaceholdersTemplate({ corpo: disparo.mensagem });
  }, [disparoTemplateSelecionado, disparo.mensagem]);

  const disparoUsuarioSelecionado = useMemo(() => {
    if (!disparo.id_usuario) return null;
    return convidados.find((c) => String(c.id) === String(disparo.id_usuario)) || null;
  }, [disparo.id_usuario, convidados]);

  const disparoPreview = useMemo(
    () =>
      renderPreviewPlaceholders(
        disparo.mensagem,
        disparoUsuarioSelecionado,
        disparo.placeholders
      ),
    [disparo.mensagem, disparo.placeholders, disparoUsuarioSelecionado]
  );

  const convidadosFiltrados = useMemo(() => {
    const tipos = form.tipos_usuarios?.length
      ? form.tipos_usuarios
      : TIPOS_USUARIO_OPCOES.map((t) => t.value);
    const base = convidados.filter((c) => tipos.includes(c.tipo));
    const q = buscaDest.trim().toLowerCase();
    if (!q) return base.slice(0, 80);
    return base
      .filter((c) => {
        const blob = `${c.nome || ""} ${c.empresa || ""} ${c.celular || ""} ${c.email || ""} ${c.tipo || ""}`.toLowerCase();
        return blob.includes(q);
      })
      .slice(0, 80);
  }, [convidados, buscaDest, form.tipos_usuarios]);

  const previewUsuario = useMemo(() => {
    if (form.ids_usuarios?.length === 1) {
      return convidados.find((c) => c.id === form.ids_usuarios[0]) || null;
    }
    return null;
  }, [form.ids_usuarios, convidados]);

  const abrirNova = () => {
    setForm(formVazio());
    setTab("form");
  };

  const abrirEditar = (msg) => {
    const tipos =
      msg.tiposUsuarios ||
      msg.tipos_usuarios ||
      ["convidado"];
    setForm({
      id: msg.id,
      mensagem: msg.mensagem || "",
      objetivo: msg.objetivo || "",
      quantidade_envios: msg.quantidadeEnvios ?? msg.quantidade_envios ?? 0,
      publico: msg.publico || "todos",
      tipos_usuarios: Array.isArray(tipos) && tipos.length ? tipos : ["convidado"],
      data_hora_envio: toDatetimeLocalValue(msg.dataHoraEnvio || msg.data_hora_envio),
      status: msg.status === "enviada" ? "agendada" : msg.status || "agendada",
      id_template: msg.idTemplate || msg.id_template || "",
      links: Array.isArray(msg.links) && msg.links.length ? msg.links : [{ url: "", rotulo: "" }],
      ids_usuarios: (msg.destinatarios || []).map((d) => d.idUsuario || d.id_usuario || d.usuario?.id).filter(Boolean),
      imagemFile: null,
      imagemPreview: msg.imagemUrl || msg.imagem_url || null,
    });
    setTab("form");
  };

  const toggleTipoUsuario = (tipo) => {
    setForm((prev) => {
      const exists = prev.tipos_usuarios.includes(tipo);
      const next = exists
        ? prev.tipos_usuarios.filter((t) => t !== tipo)
        : [...prev.tipos_usuarios, tipo];
      return {
        ...prev,
        tipos_usuarios: next.length ? next : ["convidado"],
        // limpa seleção que ficou fora do tipo
        ids_usuarios: prev.ids_usuarios.filter((id) => {
          const user = convidados.find((c) => c.id === id);
          return user && next.includes(user.tipo);
        }),
      };
    });
  };

  const toggleDestinatario = (id) => {
    setForm((prev) => {
      const exists = prev.ids_usuarios.includes(id);
      return {
        ...prev,
        ids_usuarios: exists
          ? prev.ids_usuarios.filter((x) => x !== id)
          : [...prev.ids_usuarios, id],
      };
    });
  };

  const buildMensagemFormData = () => {
    const fd = new FormData();
    fd.append("mensagem", form.mensagem);
    fd.append("objetivo", form.objetivo || "");
    fd.append("quantidade_envios", String(Number(form.quantidade_envios) || 0));
    fd.append("publico", form.publico);
    fd.append("tipos_usuarios", JSON.stringify(form.tipos_usuarios || ["convidado"]));
    fd.append("status", form.status || "agendada");
    if (form.data_hora_envio) {
      const dataHoraIso = datetimeLocalParaIso(form.data_hora_envio);
      if (dataHoraIso) fd.append("data_hora_envio", dataHoraIso);
    }
    if (form.id_template) fd.append("id_template", String(form.id_template));
    const links = (form.links || []).filter((l) => l.url?.trim());
    fd.append("links", JSON.stringify(links));
    if (form.publico === "especifico") {
      fd.append("ids_usuarios", JSON.stringify(form.ids_usuarios));
    }
    if (form.imagemFile) fd.append("imagem", form.imagemFile);
    return fd;
  };

  const salvarMensagem = async () => {
    if (!form.id_template) {
      toast.error("Selecione um template aprovado");
      return;
    }
    if (!form.mensagem.trim()) {
      toast.error("Template sem conteúdo de mensagem");
      return;
    }
    if (!form.tipos_usuarios?.length) {
      toast.error("Selecione ao menos um tipo de usuário");
      return;
    }
    if (form.publico === "especifico" && form.ids_usuarios.length === 0) {
      toast.error("Selecione ao menos um destinatário");
      return;
    }
    setSalvando(true);
    try {
      const fd = buildMensagemFormData();
      if (form.id) {
        await atualizarNotificacaoWhatsapp(form.id, fd);
        toast.success("Mensagem atualizada");
      } else {
        await criarNotificacaoWhatsapp(fd);
        toast.success("Mensagem criada");
      }
      setForm(formVazio());
      setTab("lista");
      await carregar();
    } catch (error) {
      toast.error(error?.response?.data?.mensagem || error.message || "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  const handleDisparar = async (id) => {
    if (!window.confirm("Disparar esta mensagem agora via Twilio?")) return;
    try {
      const res = await dispararNotificacaoWhatsapp(id);
      const naFila = res.na_fila ?? 0;
      toast.success(
        res.info ||
          (naFila > 0
            ? `Disparo iniciado: ${res.enviados || 0} enviados neste lote, ${naFila} na fila`
            : `Disparo: ${res.enviados || 0} enviados, ${res.erros || 0} erros`)
      );
      await carregar();
    } catch (error) {
      toast.error(error?.response?.data?.mensagem || "Erro ao disparar");
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Excluir/cancelar esta mensagem?")) return;
    try {
      await excluirNotificacaoWhatsapp(id);
      toast.success("Mensagem removida");
      await carregar();
    } catch (error) {
      toast.error(error?.response?.data?.mensagem || "Erro ao excluir");
    }
  };

  const abrirEnvios = async (msg) => {
    try {
      const items = await listarEnviosNotificacaoWhatsapp(msg.id);
      setEnviosModal({
        open: true,
        items,
        titulo: msg.objetivo || `Mensagem #${msg.id}`,
      });
    } catch {
      toast.error("Erro ao carregar envios");
    }
  };

  const salvarTemplate = async () => {
    if (!templateForm.nome.trim() || !templateForm.corpo.trim()) {
      toast.error("Nome e corpo do template são obrigatórios");
      return;
    }
    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append("nome", templateForm.nome);
      fd.append("friendly_name", templateForm.friendly_name || templateForm.nome);
      fd.append("corpo", templateForm.corpo);
      fd.append("categoria", templateForm.categoria);
      const links = (templateForm.links || []).filter((l) => l.url?.trim());
      fd.append("links", JSON.stringify(links));
      if (templateForm.imagemFile) {
        fd.append("imagem", templateForm.imagemFile);
        fd.append("header_tipo", "media");
      }
      await criarTemplateWhatsapp(fd);
      toast.success("Template criado no Twilio");
      setTemplateForm(templateVazio());
      await carregar();
    } catch (error) {
      toast.error(error.message || "Erro ao criar template");
    } finally {
      setSalvando(false);
    }
  };

  const handleSubmeter = async (id) => {
    try {
      await submeterTemplateWhatsapp(id);
      toast.success("Template submetido à Meta");
      await carregar();
    } catch (error) {
      toast.error(error.message || "Erro ao submeter");
    }
  };

  const handleSincronizar = async (id) => {
    try {
      await sincronizarTemplateWhatsapp(id);
      toast.success("Status sincronizado");
      await carregar();
    } catch (error) {
      toast.error(error.message || "Erro ao sincronizar");
    }
  };

  const handleImportarTwilio = async () => {
    setSalvando(true);
    try {
      const res = await importarTemplatesTwilio();
      toast.success(
        res.mensagem ||
          `Importados: ${res.criados || 0} novos, ${res.atualizados || 0} atualizados`
      );
      if (Array.isArray(res.templates)) {
        const ordenados = [...res.templates].sort((a, b) => {
          const da = new Date(a.createdAt || a.created_at || 0).getTime();
          const db = new Date(b.createdAt || b.created_at || 0).getTime();
          if (db !== da) return db - da;
          return (b.id || 0) - (a.id || 0);
        });
        setTemplates(ordenados);
      } else {
        await carregar();
      }
    } catch (error) {
      toast.error(error.message || "Erro ao importar da Twilio");
    } finally {
      setSalvando(false);
    }
  };

  const enviarDisparoRapido = async () => {
    if (!disparo.id_template && !disparo.mensagem.trim()) {
      toast.error("Informe a mensagem ou selecione um template");
      return;
    }
    if (!disparo.numero && !disparo.id_usuario) {
      toast.error("Informe o número ou selecione um convidado");
      return;
    }
    if (disparoPlaceholdersKeys.length > 0) {
      const faltando = disparoPlaceholdersKeys.filter(
        (k) => !String(disparo.placeholders?.[k] || "").trim()
      );
      if (faltando.length) {
        toast.error(`Preencha os placeholders: ${faltando.join(", ")}`);
        return;
      }
    }
    setSalvando(true);
    try {
      await disparoRapidoWhatsapp({
        mensagem: disparo.mensagem || undefined,
        numero: disparo.numero || undefined,
        id_usuario: disparo.id_usuario ? Number(disparo.id_usuario) : undefined,
        id_template: disparo.id_template ? Number(disparo.id_template) : undefined,
        placeholders:
          disparoPlaceholdersKeys.length > 0 ? disparo.placeholders : undefined,
      });
      toast.success("Mensagem enviada via Twilio");
      setDisparo({
        mensagem: "",
        numero: "",
        id_usuario: "",
        id_template: "",
        placeholders: {},
      });
    } catch (error) {
      toast.error(error.message || "Erro no disparo");
    } finally {
      setSalvando(false);
    }
  };

  const selecionarTemplateForm = (idTemplate) => {
    if (!idTemplate) {
      setForm((prev) => ({ ...prev, id_template: "", mensagem: "" }));
      return;
    }
    const tpl = templates.find((t) => String(t.id) === String(idTemplate));
    if (!tpl) return;
    setForm((prev) => ({
      ...prev,
      id_template: String(idTemplate),
      mensagem: tpl.corpo || "",
    }));
  };

  const selecionarTemplateDisparo = (idTemplate) => {
    if (!idTemplate) {
      setDisparo((prev) => ({
        ...prev,
        id_template: "",
        placeholders: {},
      }));
      return;
    }
    const tpl = templates.find((t) => String(t.id) === String(idTemplate));
    if (!tpl) return;
    const keys = extrairPlaceholdersTemplate(tpl);
    const usuario = disparo.id_usuario
      ? convidados.find((c) => String(c.id) === String(disparo.id_usuario))
      : null;
    const base = valoresPlaceholderDoUsuario(usuario);
    const placeholders = {};
    keys.forEach((k) => {
      placeholders[k] = base[k] || "";
    });
    setDisparo((prev) => ({
      ...prev,
      id_template: String(idTemplate),
      mensagem: tpl.corpo || "",
      placeholders,
    }));
  };

  const selecionarUsuarioDisparo = (idUsuario) => {
    const usuario = idUsuario
      ? convidados.find((c) => String(c.id) === String(idUsuario))
      : null;
    const base = valoresPlaceholderDoUsuario(usuario);
    setDisparo((prev) => {
      const keys =
        Object.keys(prev.placeholders || {}).length > 0
          ? Object.keys(prev.placeholders)
          : extrairPlaceholdersTemplate(
              prev.id_template
                ? templates.find((t) => String(t.id) === String(prev.id_template))
                : { corpo: prev.mensagem }
            );
      const placeholders = { ...prev.placeholders };
      keys.forEach((k) => {
        if (base[k]) placeholders[k] = base[k];
      });
      return {
        ...prev,
        id_usuario: idUsuario,
        placeholders,
      };
    });
  };

  return (
    <div className="container-adm-main">
      <div className="apl-page apl-page--wide-form">
        <header className="apl-header">
          <div className="apl-header-text">
            <h1>Notificações WhatsApp</h1>
            <p>
              Agende envios, gerencie templates Meta via Twilio e dispare
              mensagens para convidados. Placeholders: {"{{NOME}}"}, {"{{TIPO}}"}.
            </p>
          </div>
          <div className="apl-toolbar">
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft /> Voltar
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={carregar}
              disabled={loading}
            >
              {loading ? <FaSpinner className="fa-spin" /> : <FaSync />} Atualizar
            </button>
            <button type="button" className="apl-btn apl-btn--primary" onClick={abrirNova}>
              <FaPlus /> Nova mensagem
            </button>
          </div>
        </header>

        <div className="anw-tabs" role="tablist">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              className={`anw-tab ${tab === id ? "anw-tab--active" : ""}`}
              onClick={() => setTab(id)}
            >
              <Icon /> {label}
            </button>
          ))}
        </div>

        {tab === "lista" && (
          <section className="apl-card">
            {loading ? (
              <div className="apl-empty">
                <FaSpinner className="fa-spin" /> Carregando…
              </div>
            ) : mensagens.length === 0 ? (
              <div className="apl-empty">
                <p>Nenhuma mensagem cadastrada.</p>
                <button type="button" className="apl-btn apl-btn--primary" onClick={abrirNova}>
                  Criar primeira mensagem
                </button>
              </div>
            ) : (
              <div className="apl-table-wrap">
                <table className="apl-table">
                  <thead>
                    <tr>
                      <th>Objetivo</th>
                      <th>Público</th>
                      <th>Data/hora</th>
                      <th>Template</th>
                      <th>Status</th>
                      <th>Envios</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mensagens.map((msg) => (
                      <tr key={msg.id}>
                        <td>
                          <strong>{msg.objetivo || `Mensagem #${msg.id}`}</strong>
                          <div className="anw-hint">
                            {(msg.mensagem || "").slice(0, 80)}
                            {(msg.mensagem || "").length > 80 ? "…" : ""}
                          </div>
                        </td>
                        <td>
                          {msg.publico === "todos"
                            ? `Todos (${(msg.tiposUsuarios || msg.tipos_usuarios || ["convidado"]).join(", ")})`
                            : `${(msg.destinatarios || []).length} específico(s)`}
                        </td>
                        <td>{formatDateTime(msg.dataHoraEnvio || msg.data_hora_envio)}</td>
                        <td>
                          {msg.template?.friendlyName ||
                            msg.template?.friendly_name ||
                            msg.template?.nome ||
                            "—"}
                        </td>
                        <td>
                          <Badge status={msg.status} />
                        </td>
                        <td>
                          {msg.enviadosCount ?? msg.enviados_count ?? 0}/
                          {msg.errosCount ?? msg.erros_count ?? 0} err
                        </td>
                        <td>
                          <div className="anw-actions-inline">
                            <button
                              type="button"
                              className="apl-btn apl-btn--ghost apl-btn--sm"
                              onClick={() => abrirEditar(msg)}
                              title="Editar"
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="apl-btn apl-btn--ghost apl-btn--sm"
                              onClick={() => handleDisparar(msg.id)}
                              title="Disparar agora"
                              disabled={msg.status === "enviada" || msg.status === "enviando"}
                            >
                              <FaPaperPlane />
                            </button>
                            <button
                              type="button"
                              className="apl-btn apl-btn--ghost apl-btn--sm"
                              onClick={() => abrirEnvios(msg)}
                              title="Ver envios"
                            >
                              Log
                            </button>
                            <button
                              type="button"
                              className="apl-btn apl-btn--ghost apl-btn--sm"
                              onClick={() => handleExcluir(msg.id)}
                              title="Excluir"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === "form" && (
          <section className="apl-card apf-form-card">
            <h2 className="apf-section-title">
              {form.id ? `Editar mensagem #${form.id}` : "Nova mensagem"}
            </h2>
            <div className="anw-form-grid">
              <div className="anw-full">
                <label className="anw-label">Template aprovado</label>
                <select
                  className="anw-select"
                  value={form.id_template}
                  onChange={(e) => selecionarTemplateForm(e.target.value)}
                >
                  <option value="">Selecione um template…</option>
                  {templatesAprovados.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.friendlyName || t.friendly_name || t.nome}
                    </option>
                  ))}
                </select>
                <div className="anw-hint">
                  Apenas templates aprovados pela Meta podem ser enviados.
                </div>
              </div>

              {form.mensagem && (
                <div className="anw-full">
                  <div className="anw-preview">
                    <strong>Preview:</strong>
                    <PreviewWhatsapp
                      texto={renderPreviewPlaceholders(form.mensagem, previewUsuario)}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="anw-label">Objetivo</label>
                <input
                  className="anw-input"
                  value={form.objetivo}
                  onChange={(e) => setForm({ ...form, objetivo: e.target.value })}
                  placeholder="Ex.: Lembrete check-in"
                />
              </div>

              <div>
                <label className="anw-label">Quantidade de envios</label>
                <input
                  type="number"
                  min="0"
                  className="anw-input"
                  value={form.quantidade_envios}
                  onChange={(e) =>
                    setForm({ ...form, quantidade_envios: e.target.value })
                  }
                />
                <div className="anw-hint">0 = sem limite (todos os destinatários)</div>
              </div>

              <div>
                <label className="anw-label">Data e hora de envio</label>
                <input
                  type="datetime-local"
                  className="anw-input"
                  value={form.data_hora_envio}
                  onChange={(e) =>
                    setForm({ ...form, data_hora_envio: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="anw-label">Status</label>
                <select
                  className="anw-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="rascunho">Rascunho</option>
                  <option value="agendada">Agendada</option>
                </select>
              </div>

              <div className="anw-full">
                <label className="anw-label">Público</label>
                <div className="anw-radio-row">
                  <label>
                    <input
                      type="radio"
                      checked={form.publico === "todos"}
                      onChange={() => setForm({ ...form, publico: "todos", ids_usuarios: [] })}
                    />
                    Todos
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={form.publico === "especifico"}
                      onChange={() => setForm({ ...form, publico: "especifico" })}
                    />
                    Usuário(s) específico(s)
                  </label>
                </div>
              </div>

              <div className="anw-full">
                <label className="anw-label">Tipo de usuário</label>
                <div className="anw-radio-row">
                  {TIPOS_USUARIO_OPCOES.map((tipo) => (
                    <label key={tipo.value}>
                      <input
                        type="checkbox"
                        checked={form.tipos_usuarios.includes(tipo.value)}
                        onChange={() => toggleTipoUsuario(tipo.value)}
                      />
                      {tipo.label}
                    </label>
                  ))}
                </div>
                <div className="anw-hint">
                  {form.publico === "todos"
                    ? "No modo Todos, a mensagem será enviada a todos os usuários dos tipos marcados que tenham celular."
                    : "No modo específico, a lista de destinatários é filtrada pelos tipos marcados."}
                </div>
              </div>

              {form.publico === "especifico" && (
                <div className="anw-full">
                  <label className="anw-label">
                    Destinatários ({form.ids_usuarios.length} selecionado(s))
                  </label>
                  <input
                    className="anw-input"
                    style={{ marginBottom: "0.5rem" }}
                    placeholder="Buscar por nome, empresa ou celular…"
                    value={buscaDest}
                    onChange={(e) => setBuscaDest(e.target.value)}
                  />
                  <div className="anw-destinatarios">
                    {convidadosFiltrados.map((c) => (
                      <label key={c.id} className="anw-destinatario-item">
                        <input
                          type="checkbox"
                          checked={form.ids_usuarios.includes(c.id)}
                          onChange={() => toggleDestinatario(c.id)}
                        />
                        <span>
                          <strong>{c.nome}</strong>
                          <div className="anw-hint">
                            {c.empresa || "—"} · {c.celular || "sem celular"} · {c.tipo}
                          </div>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="anw-full">
                <label className="anw-label">Links</label>
                {(form.links || []).map((link, idx) => (
                  <div className="anw-links-row" key={idx}>
                    <input
                      className="anw-input"
                      placeholder="URL"
                      value={link.url}
                      onChange={(e) => {
                        const links = [...form.links];
                        links[idx] = { ...links[idx], url: e.target.value };
                        setForm({ ...form, links });
                      }}
                    />
                    <input
                      className="anw-input"
                      placeholder="Rótulo"
                      value={link.rotulo || ""}
                      onChange={(e) => {
                        const links = [...form.links];
                        links[idx] = { ...links[idx], rotulo: e.target.value };
                        setForm({ ...form, links });
                      }}
                    />
                    <button
                      type="button"
                      className="apl-btn apl-btn--ghost apl-btn--sm"
                      onClick={() =>
                        setForm({
                          ...form,
                          links: form.links.filter((_, i) => i !== idx),
                        })
                      }
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost apl-btn--sm"
                  onClick={() =>
                    setForm({
                      ...form,
                      links: [...(form.links || []), { url: "", rotulo: "" }],
                    })
                  }
                >
                  <FaPlus /> Adicionar link
                </button>
              </div>

              <div className="anw-full">
                <label className="anw-label">Imagem anexada</label>
                <label className="apf-upload-zone" style={{ maxWidth: 320 }}>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setForm({
                        ...form,
                        imagemFile: file,
                        imagemPreview: URL.createObjectURL(file),
                      });
                    }}
                  />
                  {form.imagemPreview ? (
                    <div className="apf-upload-preview">
                      <img
                        src={form.imagemPreview}
                        alt="Preview"
                        className="apf-upload-preview-img"
                      />
                    </div>
                  ) : (
                    <div className="apf-upload-empty">
                      <div className="apf-upload-icon-wrap">
                        <FaUpload />
                      </div>
                      <p className="apf-upload-title">Anexar imagem</p>
                      <span className="apf-upload-browse">Escolher arquivo</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div className="apl-toolbar" style={{ marginTop: "1.25rem" }}>
              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => {
                  setForm(formVazio());
                  setTab("lista");
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="apl-btn apl-btn--primary"
                onClick={salvarMensagem}
                disabled={salvando}
              >
                {salvando ? <FaSpinner className="fa-spin" /> : <FaCheck />} Salvar
              </button>
            </div>
          </section>
        )}

        {tab === "templates" && (
          <>
            <section className="apl-card apf-form-card anw-collapse-card">
              <button
                type="button"
                className="anw-collapse-toggle"
                onClick={() => setTemplateFormAberto((v) => !v)}
                aria-expanded={templateFormAberto}
              >
                <h2 className="apf-section-title" style={{ margin: 0, borderBottom: 0 }}>
                  Novo Content Template (Twilio → Meta)
                </h2>
                <span className="anw-collapse-hint">
                  {templateFormAberto ? "Recolher" : "Expandir"}
                  {templateFormAberto ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </button>

              {templateFormAberto && (
                <>
                  <div className="anw-form-grid" style={{ marginTop: "1rem" }}>
                    <div>
                      <label className="anw-label">Nome (slug)</label>
                      <input
                        className="anw-input"
                        value={templateForm.nome}
                        onChange={(e) =>
                          setTemplateForm({ ...templateForm, nome: e.target.value })
                        }
                        placeholder="lembrete_checkin"
                      />
                    </div>
                    <div>
                      <label className="anw-label">Nome amigável</label>
                      <input
                        className="anw-input"
                        value={templateForm.friendly_name}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            friendly_name: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label className="anw-label">Categoria</label>
                      <select
                        className="anw-select"
                        value={templateForm.categoria}
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            categoria: e.target.value,
                          })
                        }
                      >
                        <option value="UTILITY">UTILITY</option>
                        <option value="MARKETING">MARKETING</option>
                        <option value="AUTHENTICATION">AUTHENTICATION</option>
                      </select>
                    </div>
                    <div className="anw-full">
                      <label className="anw-label">Corpo</label>
                      <FormatToolbarWhatsapp
                        onBold={() =>
                          aplicarFormatacaoWhatsapp(
                            refCorpoTemplate.current,
                            templateForm.corpo,
                            (v) => setTemplateForm({ ...templateForm, corpo: v }),
                            "*",
                            "*"
                          )
                        }
                        onItalic={() =>
                          aplicarFormatacaoWhatsapp(
                            refCorpoTemplate.current,
                            templateForm.corpo,
                            (v) => setTemplateForm({ ...templateForm, corpo: v }),
                            "_",
                            "_"
                          )
                        }
                        onStrike={() =>
                          aplicarFormatacaoWhatsapp(
                            refCorpoTemplate.current,
                            templateForm.corpo,
                            (v) => setTemplateForm({ ...templateForm, corpo: v }),
                            "~",
                            "~"
                          )
                        }
                      />
                      <textarea
                        ref={refCorpoTemplate}
                        className="anw-textarea"
                        value={templateForm.corpo}
                        onChange={(e) =>
                          setTemplateForm({ ...templateForm, corpo: e.target.value })
                        }
                        placeholder={"👋 *Bem-vindo(a)* ao SBF 2026!\nOlá {{NOME}}..."}
                      />
                      <div className="anw-hint">
                        Use {"{{NOME}}"}, {"{{TIPO}}"} — convertidos para variáveis Twilio.
                        Negrito no WhatsApp: *texto*.
                      </div>
                      <div className="anw-preview" style={{ marginTop: "0.5rem" }}>
                        <strong>Preview:</strong>
                        <PreviewWhatsapp texto={templateForm.corpo} />
                      </div>
                    </div>
                    <div className="anw-full">
                      <label className="anw-label">Links / botões</label>
                      {(templateForm.links || []).map((link, idx) => (
                        <div className="anw-links-row" key={idx}>
                          <input
                            className="anw-input"
                            placeholder="URL"
                            value={link.url}
                            onChange={(e) => {
                              const links = [...templateForm.links];
                              links[idx] = { ...links[idx], url: e.target.value };
                              setTemplateForm({ ...templateForm, links });
                            }}
                          />
                          <input
                            className="anw-input"
                            placeholder="Rótulo"
                            value={link.rotulo || ""}
                            onChange={(e) => {
                              const links = [...templateForm.links];
                              links[idx] = { ...links[idx], rotulo: e.target.value };
                              setTemplateForm({ ...templateForm, links });
                            }}
                          />
                          <button
                            type="button"
                            className="apl-btn apl-btn--ghost apl-btn--sm"
                            onClick={() =>
                              setTemplateForm({
                                ...templateForm,
                                links: templateForm.links.filter((_, i) => i !== idx),
                              })
                            }
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="apl-btn apl-btn--ghost apl-btn--sm"
                        onClick={() =>
                          setTemplateForm({
                            ...templateForm,
                            links: [...(templateForm.links || []), { url: "", rotulo: "" }],
                          })
                        }
                      >
                        <FaPlus /> Link
                      </button>
                    </div>
                    <div>
                      <label className="anw-label">Imagem do header (opcional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          setTemplateForm({
                            ...templateForm,
                            imagemFile: e.target.files?.[0] || null,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="apl-toolbar" style={{ marginTop: "1rem" }}>
                    <button
                      type="button"
                      className="apl-btn apl-btn--primary"
                      onClick={salvarTemplate}
                      disabled={salvando}
                    >
                      {salvando ? <FaSpinner className="fa-spin" /> : <FaPlus />} Criar no Twilio
                    </button>
                  </div>
                </>
              )}
            </section>

            <section className="apl-card apf-form-card" style={{ marginTop: "1.25rem" }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  marginBottom: "0.75rem",
                }}
              >
                <h2 className="apf-section-title" style={{ margin: 0, flex: 1 }}>
                  Templates cadastrados
                </h2>
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost apl-btn--sm"
                  onClick={handleImportarTwilio}
                  disabled={salvando}
                  title="Buscar templates já existentes na conta Twilio"
                >
                  {salvando ? <FaSpinner className="fa-spin" /> : <FaSync />} Importar da Twilio
                </button>
              </div>
              {templates.length === 0 ? (
                <div className="apl-empty">
                  <p>Nenhum template ainda.</p>
                  <button
                    type="button"
                    className="apl-btn apl-btn--primary"
                    onClick={handleImportarTwilio}
                    disabled={salvando}
                  >
                    {salvando ? <FaSpinner className="fa-spin" /> : <FaSync />} Buscar na Twilio
                  </button>
                </div>
              ) : (
                <div className="apl-table-wrap">
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Categoria</th>
                        <th>SID</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map((t) => {
                        const status = t.statusAprovacao || t.status_aprovacao;
                        return (
                          <tr key={t.id}>
                            <td>
                              <strong>{t.friendlyName || t.friendly_name || t.nome}</strong>
                              <div className="anw-hint">{t.nome}</div>
                              {(t.rejectionReason || t.rejection_reason) && (
                                <div className="anw-hint" style={{ color: "#b91c1c" }}>
                                  {t.rejectionReason || t.rejection_reason}
                                </div>
                              )}
                            </td>
                            <td>{t.categoria}</td>
                            <td>
                              <code style={{ fontSize: "0.7rem" }}>
                                {t.twilioContentSid || t.twilio_content_sid || "—"}
                              </code>
                            </td>
                            <td>
                              <Badge status={status} />
                            </td>
                            <td>
                              <div className="anw-actions-inline">
                                <button
                                  type="button"
                                  className="apl-btn apl-btn--ghost apl-btn--sm"
                                  onClick={() => setTemplatePreview(t)}
                                  title="Preview do template"
                                >
                                  <FaEye /> Preview
                                </button>
                                {status === "unsubmitted" && (
                                  <button
                                    type="button"
                                    className="apl-btn apl-btn--primary apl-btn--sm"
                                    onClick={() => handleSubmeter(t.id)}
                                  >
                                    Submeter Meta
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="apl-btn apl-btn--ghost apl-btn--sm"
                                  onClick={() => handleSincronizar(t.id)}
                                >
                                  <FaSync /> Sync
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {tab === "disparo" && (
          <section className="apl-card apf-form-card">
            <h2 className="apf-section-title">Disparo rápido (Twilio)</h2>
            <p className="anw-hint" style={{ marginBottom: "1rem" }}>
              Prefira um template aprovado para envios fora da janela de 24h. Os placeholders
              do template aparecem como campos para preenchimento.
            </p>
            <div className="anw-form-grid">
              <div className="anw-full">
                <label className="anw-label">Template aprovado</label>
                <select
                  className="anw-select"
                  value={disparo.id_template}
                  onChange={(e) => selecionarTemplateDisparo(e.target.value)}
                >
                  <option value="">Nenhum (texto livre)</option>
                  {templatesAprovados.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.friendlyName || t.friendly_name || t.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="anw-full">
                <label className="anw-label">Mensagem</label>
                {!disparo.id_template && (
                  <FormatToolbarWhatsapp
                    onBold={() =>
                      aplicarFormatacaoWhatsapp(
                        refMensagemDisparo.current,
                        disparo.mensagem,
                        (v) => setDisparo({ ...disparo, mensagem: v }),
                        "*",
                        "*"
                      )
                    }
                    onItalic={() =>
                      aplicarFormatacaoWhatsapp(
                        refMensagemDisparo.current,
                        disparo.mensagem,
                        (v) => setDisparo({ ...disparo, mensagem: v }),
                        "_",
                        "_"
                      )
                    }
                    onStrike={() =>
                      aplicarFormatacaoWhatsapp(
                        refMensagemDisparo.current,
                        disparo.mensagem,
                        (v) => setDisparo({ ...disparo, mensagem: v }),
                        "~",
                        "~"
                      )
                    }
                  />
                )}
                <textarea
                  ref={refMensagemDisparo}
                  className="anw-textarea"
                  value={disparo.mensagem}
                  onChange={(e) => {
                    const mensagem = e.target.value;
                    const keys = disparo.id_template
                      ? disparoPlaceholdersKeys
                      : extrairPlaceholdersTemplate({ corpo: mensagem });
                    setDisparo((prev) => {
                      const placeholders = { ...prev.placeholders };
                      keys.forEach((k) => {
                        if (placeholders[k] === undefined) placeholders[k] = "";
                      });
                      Object.keys(placeholders).forEach((k) => {
                        if (!keys.includes(k)) delete placeholders[k];
                      });
                      return { ...prev, mensagem, placeholders };
                    });
                  }}
                  placeholder="Olá {{NOME}}..."
                  readOnly={!!disparo.id_template}
                />
                {disparo.id_template && (
                  <div className="anw-hint">
                    Mensagem carregada do template (somente leitura). Edite pelos campos de placeholder.
                  </div>
                )}
              </div>

              {disparoPlaceholdersKeys.length > 0 && (
                <div className="anw-full">
                  <label className="anw-label">Placeholders</label>
                  <div className="anw-form-grid">
                    {disparoPlaceholdersKeys.map((key) => (
                      <div key={key}>
                        <label className="anw-label">{`{{${key}}}`}</label>
                        <input
                          className="anw-input"
                          value={disparo.placeholders?.[key] || ""}
                          onChange={(e) =>
                            setDisparo((prev) => ({
                              ...prev,
                              placeholders: {
                                ...prev.placeholders,
                                [key]: e.target.value,
                              },
                            }))
                          }
                          placeholder={`Valor para ${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="anw-full">
                <label className="anw-label">Preview</label>
                <div className="anw-preview">
                  <PreviewWhatsapp texto={disparoPreview} />
                </div>
              </div>

              <div>
                <label className="anw-label">Número (com DDI)</label>
                <input
                  className="anw-input"
                  value={disparo.numero}
                  onChange={(e) => setDisparo({ ...disparo, numero: e.target.value })}
                  placeholder="5511999999999"
                />
              </div>
              <div>
                <label className="anw-label">Ou convidado</label>
                <select
                  className="anw-select"
                  value={disparo.id_usuario}
                  onChange={(e) => selecionarUsuarioDisparo(e.target.value)}
                >
                  <option value="">Selecione…</option>
                  {convidados
                    .filter((c) => c.celular)
                    .slice(0, 200)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome} — {c.celular}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="apl-toolbar" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="apl-btn apl-btn--primary"
                onClick={enviarDisparoRapido}
                disabled={salvando}
              >
                {salvando ? <FaSpinner className="fa-spin" /> : <FaPaperPlane />} Enviar agora
              </button>
            </div>
          </section>
        )}
      </div>

      <Modal
        show={enviosModal.open}
        onHide={() => setEnviosModal({ open: false, items: [], titulo: "" })}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Envios — {enviosModal.titulo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {enviosModal.items.length === 0 ? (
            <p>Nenhum envio registrado.</p>
          ) : (
            <div className="apl-table-wrap">
              <table className="apl-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Usuário</th>
                    <th>Status</th>
                    <th>SID</th>
                    <th>Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {enviosModal.items.map((e) => (
                    <tr key={e.id}>
                      <td>{e.numero}</td>
                      <td>{e.usuario?.nome || "—"}</td>
                      <td>
                        <Badge status={e.status} />
                      </td>
                      <td>
                        <code style={{ fontSize: "0.7rem" }}>
                          {e.twilioMessageSid || e.twilio_message_sid || "—"}
                        </code>
                      </td>
                      <td>{e.erro || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!templatePreview}
        onHide={() => setTemplatePreview(null)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Preview —{" "}
            {templatePreview?.friendlyName ||
              templatePreview?.friendly_name ||
              templatePreview?.nome ||
              "Template"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {templatePreview && (
            <div className="anw-template-preview">
              <div className="anw-template-preview-meta">
                <div>
                  <span className="anw-label">Slug</span>
                  <div>{templatePreview.nome}</div>
                </div>
                <div>
                  <span className="anw-label">Categoria</span>
                  <div>{templatePreview.categoria}</div>
                </div>
                <div>
                  <span className="anw-label">Status</span>
                  <div>
                    <Badge
                      status={
                        templatePreview.statusAprovacao ||
                        templatePreview.status_aprovacao
                      }
                    />
                  </div>
                </div>
                <div>
                  <span className="anw-label">SID</span>
                  <code style={{ fontSize: "0.75rem", wordBreak: "break-all" }}>
                    {templatePreview.twilioContentSid ||
                      templatePreview.twilio_content_sid ||
                      "—"}
                  </code>
                </div>
              </div>

              <div className="anw-wa-stage">
                <div className="anw-wa-bubble">
                  {(templatePreview.headerMediaUrl ||
                    templatePreview.header_media_url) && (
                    <img
                      src={
                        templatePreview.headerMediaUrl ||
                        templatePreview.header_media_url
                      }
                      alt="Header do template"
                      className="anw-wa-media"
                    />
                  )}
                  <div className="anw-wa-body">
                    <PreviewWhatsapp
                      texto={renderPreviewPlaceholders(templatePreview.corpo, {
                        nome: "João Silva",
                        tipo: "convidado",
                        empresa: "Empresa Exemplo",
                        cargo: "Diretor",
                        email: "exemplo@email.com",
                        celular: "11999999999",
                      })}
                    />
                  </div>
                  {Array.isArray(templatePreview.links) &&
                    templatePreview.links.length > 0 && (
                      <div className="anw-wa-links">
                        {templatePreview.links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="anw-wa-link"
                          >
                            {link.rotulo || link.url}
                          </a>
                        ))}
                      </div>
                    )}
                </div>
              </div>

              <p className="anw-hint" style={{ marginTop: "0.75rem" }}>
                Placeholders substituídos com dados de exemplo (ex.: {"{{NOME}}"} → João Silva).
              </p>

              {(templatePreview.variaveisMap ||
                templatePreview.variaveis_map) && (
                <div style={{ marginTop: "0.75rem" }}>
                  <span className="anw-label">Variáveis</span>
                  <pre className="anw-preview" style={{ marginTop: "0.35rem" }}>
                    {JSON.stringify(
                      templatePreview.variaveisMap ||
                        templatePreview.variaveis_map,
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setTemplatePreview(null)}
          >
            Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdmNotificacoes;
